import { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useLocation } from "wouter";
import { api, API_BASE } from "../api/client";
import type { Category } from "../types";
import { TopNav } from "../components/layout/TopNav";
import { useIsMobile } from "../hooks/useIsMobile";

export function SettingsPage() {
  const { user, logout } = useAuth();
  const [, navigate] = useLocation();
  const isMobile = useIsMobile();
  const [categories, setCategories] = useState<Category[]>([]);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#6b7280");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [exportCat, setExportCat] = useState("");

  useEffect(() => {
    api.get<Category[]>("/api/categories").then(setCategories).catch(() => {});
  }, []);

  async function addCategory() {
    if (!newName.trim()) return;
    const cat = await api.post<Category>("/api/categories", { name: newName.trim(), color: newColor });
    setCategories(prev => [...prev, cat]);
    setNewName("");
  }

  async function saveEdit(id: string) {
    const updated = await api.put<Category>(`/api/categories/${id}`, { name: editName });
    setCategories(prev => prev.map(c => (c.id === id ? updated : c)));
    setEditingId(null);
  }

  async function deleteCategory(id: string) {
    if (!confirm("Delete this category? Receipts will become uncategorised.")) return;
    await api.delete(`/api/categories/${id}`);
    setCategories(prev => prev.filter(c => c.id !== id));
  }

  function buildExportUrl(format: "csv" | "pdf") {
    const params = new URLSearchParams();
    if (exportFrom) params.set("from", exportFrom);
    if (exportTo) params.set("to", exportTo);
    if (exportCat) params.set("category_id", exportCat);
    params.set("token", localStorage.getItem("access_token") ?? "");
    return `${API_BASE}/api/export/${format}?${params}`;
  }

  if (!user) return null;

  const inp: React.CSSProperties = {
    background: "#0f172a", border: "1px solid #334155", borderRadius: "4px",
    color: "#f1f5f9", padding: "0.375rem 0.75rem", fontSize: "0.875rem",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0f172a" }}>
      <TopNav user={user} onLogout={logout} onUpload={() => navigate("/")} hideUpload={isMobile} />
      <div style={{ maxWidth: "700px", margin: "0 auto", padding: "2rem 1.5rem" }}>

        <section style={{ marginBottom: "3rem" }}>
          <h2 style={{ color: "#f1f5f9", marginBottom: "1.25rem" }}>Categories</h2>
          <div style={{ background: "#1e293b", borderRadius: "8px", overflow: "hidden", marginBottom: "1rem" }}>
            {categories.map(c => (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.75rem 1rem", borderBottom: "1px solid #0f172a" }}>
                <span style={{ width: "12px", height: "12px", borderRadius: "50%", background: c.color, flexShrink: 0 }} />
                {editingId === c.id ? (
                  <>
                    <input style={{ ...inp, flex: 1 }} value={editName} onChange={e => setEditName(e.target.value)} autoFocus />
                    <button onClick={() => saveEdit(c.id)} style={{ background: "#1d4ed8", color: "#fff", border: "none", borderRadius: "4px", padding: "0.25rem 0.75rem", cursor: "pointer" }}>Save</button>
                    <button onClick={() => setEditingId(null)} style={{ background: "#334155", color: "#94a3b8", border: "none", borderRadius: "4px", padding: "0.25rem 0.75rem", cursor: "pointer" }}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span style={{ color: "#f1f5f9", flex: 1 }}>{c.name}</span>
                    {c.is_default && <span style={{ color: "#64748b", fontSize: "0.75rem" }}>default</span>}
                    <button onClick={() => { setEditingId(c.id); setEditName(c.name); }} style={{ background: "none", border: "none", color: "#3b82f6", cursor: "pointer", fontSize: "0.8rem" }}>Edit</button>
                    {!c.is_default && (
                      <button onClick={() => deleteCategory(c.id)} style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", fontSize: "0.8rem" }}>Delete</button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <input style={{ ...inp, flex: 1 }} placeholder="New category name" value={newName}
              onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === "Enter" && addCategory()} />
            <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)}
              style={{ width: "40px", padding: "2px", border: "1px solid #334155", borderRadius: "4px", background: "#0f172a", cursor: "pointer" }} />
            <button onClick={addCategory} style={{ background: "#1d4ed8", color: "#fff", border: "none", borderRadius: "4px", padding: "0.375rem 1rem", cursor: "pointer" }}>Add</button>
          </div>
        </section>

        <section>
          <h2 style={{ color: "#f1f5f9", marginBottom: "1.25rem" }}>Export for accountant</h2>
          <div style={{ background: "#1e293b", borderRadius: "8px", padding: "1.25rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
              <div>
                <label style={{ color: "#94a3b8", fontSize: "0.8rem", display: "block", marginBottom: "0.25rem" }}>From date</label>
                <input type="date" style={inp} value={exportFrom} onChange={e => setExportFrom(e.target.value)} />
              </div>
              <div>
                <label style={{ color: "#94a3b8", fontSize: "0.8rem", display: "block", marginBottom: "0.25rem" }}>To date</label>
                <input type="date" style={inp} value={exportTo} onChange={e => setExportTo(e.target.value)} />
              </div>
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ color: "#94a3b8", fontSize: "0.8rem", display: "block", marginBottom: "0.25rem" }}>Category (optional)</label>
              <select style={{ ...inp, width: "100%" }} value={exportCat} onChange={e => setExportCat(e.target.value)}>
                <option value="">All categories</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <a href={buildExportUrl("csv")} target="_blank" rel="noreferrer"
                style={{ flex: 1, padding: "0.625rem", background: "#166534", color: "#4ade80", borderRadius: "4px", textAlign: "center", textDecoration: "none", fontWeight: 500 }}>
                Download CSV
              </a>
              <a href={buildExportUrl("pdf")} target="_blank" rel="noreferrer"
                style={{ flex: 1, padding: "0.625rem", background: "#1d3461", color: "#93c5fd", borderRadius: "4px", textAlign: "center", textDecoration: "none", fontWeight: 500 }}>
                Download PDF
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
